import React, { CSSProperties, useState, useEffect } from 'react';

import Label from '@components/inputs/text/Label';
import { UserFormStates, useUserJourneyContext } from '@context/UserJourneyContext';
import DropDownMenu, { IChoice } from "@components/inputs/DropDownMenu";
import nationalities from '../../SmartOpsHome/nationalities';
import { isNotNullNotUndefined } from '@utils/misc/functions';
import { textBoxStyle } from '@utils/styles/textboxStyle';
import { BreadcrumbProps } from 'SmartOpsHome/breadcrumbObject';
import PHAVService from '@services/phav/PHAVService';
import useServiceHook from '@hooks/useServiceHook';
import { Country } from '@utils/constants/localisation';
import Elliptical from '@components/spinners/Elliptical/Elliptical';
import { useMsal } from '@azure/msal-react';


export default function PHAVPersonalDetails() {

    const dropDownPleaseSelect: IChoice = { label: '- please select -', value: '', enabled: false };

    const sexOptions: IChoice[] = [
        { value: 'Male', label: 'Male', enabled: true },
        { value: 'Female', label: 'Female', enabled: true },
        { value: 'Other', label: 'Other', enabled: true },
        { value: 'Do not wish to disclose', label: 'Do not wish to disclose', enabled: true },
    ];

    const yesOption: IChoice = { value: 'true', label: 'Yes', enabled: true };
    const noOption: IChoice = { value: 'false', label: 'No', enabled: true };
    const hasAltNameOptions: IChoice[] = [yesOption, noOption];

    const { formState, updateFormState } = useUserJourneyContext();
    const { personalInfo, driverDetails, addressInfo } = formState;

    const country = driverDetails.country;
    const phavModuleId = driverDetails.phavModuleId;
    const msalContext = useMsal();

    const [hasAltName, setHasAltName] = useState<Boolean>(personalInfo.alternativeName !== '' && isNotNullNotUndefined(personalInfo.alternativeName));

    function getDefaultGender(sexOptions: IChoice[]): IChoice {
        return personalInfo.gender === ''
            ? sexOptions[0]
            : sexOptions.find((gender) => gender.value === personalInfo.gender) || sexOptions[0];
    }

    function getDefaultNationality(nationalities: IChoice[]): IChoice | undefined {
        return country == 'AU'
            ? nationalities.find((nationality) => nationality.label === 'Australian')
            : nationalities.find((nationality) => nationality.label === 'British');
    }


    const defaultNationality = getDefaultNationality(nationalities);
    const defaultValue = defaultNationality ? { label: defaultNationality.label, value: defaultNationality.value } : dropDownPleaseSelect;

    const defaultGender = getDefaultGender(sexOptions);

    useEffect(() => {
        updateFormState(UserFormStates.personalInfo, { gender: defaultGender?.value });
        updateFormState(UserFormStates.personalInfo, { nationality: defaultValue?.value });
    }, []);

    const handleMobileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value;

        // Remove non-numeric characters
        value = value.replace(/[^0-9+]/g, '')

        event.target.value = value;
    };


    const handlePersonalInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        switch (e.currentTarget.name) {
            case 'firstName':
                updateFormState(UserFormStates.personalInfo, { firstName: e.currentTarget.value });
                break;
            case 'middleName':
                updateFormState(UserFormStates.personalInfo, { middleName: e.currentTarget.value });
                break;
            case 'lastName':
                updateFormState(UserFormStates.personalInfo, { lastName: e.currentTarget.value });
                break;
            case 'email':
                updateFormState(UserFormStates.personalInfo, { email: e.currentTarget.value });
                break;
            case 'hasAltName':
                if (e.currentTarget.value === 'false') {
                    setHasAltName(false);
                    updateFormState(UserFormStates.personalInfo, { alternativeName: '' });
                } else {
                    setHasAltName(true);
                }
                break;
            case 'altName':
                updateFormState(UserFormStates.personalInfo, { alternativeName: e.currentTarget.value });
                break;
            case 'genderDropMenu':
                updateFormState(UserFormStates.personalInfo, { gender: e.currentTarget.value });
                break;
            case 'nationality':
                updateFormState(UserFormStates.personalInfo, { nationality: e.currentTarget.value });
                break;
            case 'mobile':
                updateFormState(UserFormStates.personalInfo, { mobile: e.currentTarget.value });
                break;
            case 'nationalInsuranceNumber':
                updateFormState(UserFormStates.personalInfo, { nationalInsuranceNumber: e.currentTarget.value });
                break;
        }
    };


    const handleInputNI = (event: React.ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value;

        // Remove non-alphanumeric characters
        value = value.replace(/[^a-zA-Z0-9]/g, '');

        // Ensure the value is not longer than 9 characters
        if (value.length > 9) {
            value = value.slice(0, 9);
        }

        event.target.value = value;
    }

    const getLoggedInUser = () => {
        const userDetails = msalContext.instance.getActiveAccount();
        return { name: userDetails?.name, email: userDetails?.username }
    }

    const loggedInUser = getLoggedInUser();

    const phavId = driverDetails.phavModuleId

    const [phavDetailsLoading, , , phavDetails] = useServiceHook(
        PHAVService.getPHAVRecordDetails,
        [phavId, country],
        true,
        [],
        "Could not load the PHAV record. Check it has a Driver Name (Contact) and an Email in Zoho, then try again."
    );

    useEffect(() => {
        if (phavDetails && !phavDetailsLoading) {
            updateFormState(UserFormStates.personalInfo, {
                firstName: phavDetails.First_Name || '',
                lastName: phavDetails.Last_Name || '',
                email: phavDetails.Email || '',
                mobile: phavDetails.Mobile_Number || '',
            });
            updateFormState(UserFormStates.addressInfo, {
                street: phavDetails.Address || '',
                city: phavDetails.City || '',
                postcode: phavDetails.Post_Code || '',
            });
            updateFormState(UserFormStates.driverDetails, {
                crmId: phavDetails.contact_id
            });
            updateFormState(UserFormStates.contractDetails, {
                contactOwner: loggedInUser.name,
                contactOwnerEmail: loggedInUser.email?.toLowerCase(),
            })
        }
    }, [phavDetails, phavDetailsLoading]);


    if (phavDetailsLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <Elliptical />
            </div>
        );
    }

    return (<>
        <p style={{ margin: '2.5vh 0' }}> Please review the customer's <strong>personal details</strong>, ensuring that each field is populated with the correct data.</p>
        <div style={{ margin: '2.5vh 0' }}>
            <Label
                text={'First Name'}
                styleCfg={{ largeFont: false, bold: false }}
                requiredFieldInd={true}
            />
            <input name={'firstName'} style={textBoxStyle} required value={personalInfo.firstName} onChange={handlePersonalInfoChange} />
        </div>

        <div style={{ margin: '2.5vh 0' }}>
            <Label
                text={'Middle Name'}
                styleCfg={{ largeFont: false, bold: false }}
                requiredFieldInd={false}
            />
            <input name={'middleName'} style={textBoxStyle} value={personalInfo.middleName} onChange={handlePersonalInfoChange} />
        </div>

        <div style={{ margin: '2.5vh 0' }}>
            <Label
                text={'Last Name'}
                styleCfg={{ largeFont: false, bold: false }}
                requiredFieldInd={true}
            />
            <input name={'lastName'} style={textBoxStyle} required value={personalInfo.lastName} onChange={handlePersonalInfoChange} />
        </div>

        <div style={{ margin: '2.5vh 0' }}>
            <Label
                text={'Email'}
                styleCfg={{ largeFont: false, bold: false }}
                requiredFieldInd={true}
            />
            <input name={'email'} style={textBoxStyle} type='email' required value={personalInfo.email} disabled />
        </div>

        <p style={{ margin: '2.5vh 0' }}> If you need to change the email address, please do so directly in the CRM.</p>

        <div style={{ margin: '2.5vh 0' }}>
            <Label
                text={'Are you known by an alternative name?'}
                styleCfg={{ largeFont: false, bold: false }}
                requiredFieldInd={true}
            />
            <DropDownMenu
                menuName='hasAltName'
                defaultVal={hasAltName === true ? yesOption : noOption}
                required={true}
                choices={hasAltNameOptions}
                onSelect={handlePersonalInfoChange}
            />
        </div>

        {hasAltName == true && <>
            <div style={{ margin: '2.5vh 0' }}>
                <Label
                    text={'Please specify an alternative name'}
                    styleCfg={{ largeFont: false, bold: false }}
                    requiredFieldInd={true}
                />
                <input name='altName' style={textBoxStyle} required value={personalInfo.alternativeName} onChange={handlePersonalInfoChange} />
            </div>
        </>}

        <div style={{ margin: '2.5vh 0' }}>
            <Label
                text={'Sex'}
                styleCfg={{ largeFont: false, bold: false }}
                requiredFieldInd={true}
            />
            <DropDownMenu
                menuName='genderDropMenu'
                defaultVal={{ label: personalInfo.gender, value: personalInfo.gender }}
                required={true}
                choices={sexOptions.map((item: any) => ({ label: item.label, value: item.value, enabled: true }))}
                onSelect={handlePersonalInfoChange}
            />
        </div>

        <div style={{ margin: '2.5vh 0' }}>
            <Label
                text={'Nationality'}
                styleCfg={{ largeFont: false, bold: false }}
                requiredFieldInd={true}
            />
            <DropDownMenu
                menuName='nationality'
                defaultVal={nationalities.some((item) => item.value === personalInfo.nationality)
                    ? { label: personalInfo.nationality, value: personalInfo.nationality }
                    : defaultValue}
                required={true}
                choices={[dropDownPleaseSelect, ...nationalities.map((item: any) => ({ label: item.label, value: item.value, enabled: true }))]}
                onSelect={handlePersonalInfoChange}
            />

        </div>

        <div style={{ margin: '2.5vh 0' }}>
            <Label
                text={'Phone'}
                styleCfg={{ largeFont: false, bold: false }}
                requiredFieldInd={true}
            />
            <input name={'mobile'} style={textBoxStyle} required value={personalInfo.mobile} onInput={handleMobileInput} onChange={handlePersonalInfoChange} />
        </div>

        <div style={{ margin: '2.5vh 0' }}>
            <Label
                text={'National Insurance Number'}
                styleCfg={{ largeFont: false, bold: false }}
                requiredFieldInd={true}
            />
            <input
                name={'nationalInsuranceNumber'}
                style={textBoxStyle}
                required
                value={personalInfo.nationalInsuranceNumber}
                onInput={handleInputNI}
                onChange={handlePersonalInfoChange}
            />
        </div>


    </>)
};

